import { useState } from 'react';
import {
  Button,
  Fieldset,
  HeadingText,
  Modal,
  Radio,
  StackLayout,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { BaseModalContainer } from 'components/modals/common/base-modal.component';
import { ModalActionButton } from 'components/modals/common/modal-action-button.component';

const RADIO_GROUP_NAME = 'pcp-inpatient-care';
const MOBILE_FULL_WIDTH = {
  '@mobile': { width: '100%', justifyContent: 'center' },
};

type InpatientAnswer = 'yes' | 'no';

interface InpatientCareGateModalProps {
  onProceed: () => void;
  onClose: () => void;
  canProceed?: boolean;
}

export const InpatientCareGateModal = ({
  onProceed,
  onClose,
  canProceed = true,
}: InpatientCareGateModalProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.only('mobile'));
  const [answer, setAnswer] = useState<InpatientAnswer | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);

  const handleContinue = () => {
    if (answer === 'yes') {
      setIsBlocked(true);
      return;
    }
    onProceed();
  };

  const renderTitle = (
    id: 'STR_PCP_INPATIENT_QUESTION' | 'STR_PCP_INPATIENT_BLOCKED',
  ) => (
    // @ts-ignore
    <Modal.Title>
      <HeadingText level="2" size="lg" css={{ textAlign: 'left' }}>
        {formatMessage({ id })}
      </HeadingText>
    </Modal.Title>
  );

  if (isBlocked) {
    return (
      <BaseModalContainer
        handleClose={onClose}
        footer={
          <StackLayout horizontalAlignment="end">
            <ModalActionButton
              actionType="submit"
              labelId="STR_GO_TO_CARE_PAGE"
              isMobile={isMobile}
              onClick={onClose}
              cssStyle={MOBILE_FULL_WIDTH}
            />
          </StackLayout>
        }
      >
        {renderTitle('STR_PCP_INPATIENT_BLOCKED')}
      </BaseModalContainer>
    );
  }

  return (
    <BaseModalContainer
      handleClose={onClose}
      footer={
        <StackLayout
          orientation={{ '@initial': 'horizontal', '@mobile': 'vertical' }}
          horizontalAlignment="end"
          spacing="$one"
        >
          <ModalActionButton
            actionType="cancel"
            labelId="STR_CANCEL"
            isMobile={isMobile}
            onClick={onClose}
            cssStyle={MOBILE_FULL_WIDTH}
          />
          <Button
            priority="primary"
            size={isMobile ? 'large' : 'medium'}
            css={MOBILE_FULL_WIDTH}
            onClick={handleContinue}
            disabled={answer === null || !canProceed}
          >
            {formatMessage({ id: 'STR_CONTINUE' })}
          </Button>
        </StackLayout>
      }
    >
      {renderTitle('STR_PCP_INPATIENT_QUESTION')}
      <Fieldset
        name={RADIO_GROUP_NAME}
        legend={formatMessage({ id: 'STR_PCP_INPATIENT_QUESTION' })}
        hideLegend
        required
        css={{ textAlign: 'left' }}
      >
        <StackLayout spacing="$half" horizontalAlignment="start">
          <Radio
            id={`${RADIO_GROUP_NAME}-yes`}
            name={RADIO_GROUP_NAME}
            value="yes"
            label={formatMessage({ id: 'STR_YES' })}
            checked={answer === 'yes'}
            onChange={() => setAnswer('yes')}
          />
          <Radio
            id={`${RADIO_GROUP_NAME}-no`}
            name={RADIO_GROUP_NAME}
            value="no"
            label={formatMessage({ id: 'STR_NO' })}
            checked={answer === 'no'}
            onChange={() => setAnswer('no')}
          />
        </StackLayout>
      </Fieldset>
    </BaseModalContainer>
  );
};
